// 実行時の設定。配信時に /config.json を置く（ステージごとに差し替え、ビルドはひとつ）。
// 手元の開発だけ VITE_ 環境変数を代わりに使う。

export interface RuntimeConfig {
  apiUrl: string;
  region: string;
  cognito: { userPoolId: string; clientId: string };
}

/** 形を検査し、apiUrl の末尾の / を落とす。足りなければ null */
export function normalizeConfig(raw: unknown): RuntimeConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const cognito = (r.cognito ?? {}) as Record<string, unknown>;
  const apiUrl = typeof r.apiUrl === 'string' ? r.apiUrl.trim().replace(/\/+$/, '') : '';
  const region = typeof r.region === 'string' ? r.region.trim() : '';
  const userPoolId = typeof cognito.userPoolId === 'string' ? cognito.userPoolId.trim() : '';
  const clientId = typeof cognito.clientId === 'string' ? cognito.clientId.trim() : '';
  if (!apiUrl || !region || !clientId) return null;
  return { apiUrl, region, cognito: { userPoolId, clientId } };
}

let current: RuntimeConfig | null = null;

function fromEnv(): RuntimeConfig | null {
  const env = import.meta.env;
  return normalizeConfig({
    apiUrl: env.VITE_API_URL,
    region: env.VITE_AWS_REGION,
    cognito: { userPoolId: env.VITE_COGNITO_USER_POOL_ID, clientId: env.VITE_COGNITO_CLIENT_ID },
  });
}

/** 起動時に 1 回呼ぶ。/config.json が無い・壊れているときは VITE_ 環境変数へ */
export async function loadConfig(): Promise<RuntimeConfig> {
  if (current) return current;
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok && (res.headers.get('content-type') ?? '').includes('json')) {
      const c = normalizeConfig(await res.json());
      if (c) return (current = c);
    }
  } catch {
    // 取れなければ環境変数へ
  }
  const c = fromEnv();
  if (c) return (current = c);
  throw new Error('設定（/config.json）を読み込めませんでした。');
}

export function getConfig(): RuntimeConfig {
  if (!current) throw new Error('設定がまだ読み込まれていません');
  return current;
}
